import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyDocumentiService from "../../../services/safety/SafetyDocumentiService";

const EMPTY_DOC = {
    id_tipo_doc: "",
    titolo: "",
    numero_protocollo: "",
    ente_emittente: "",
    data_emissione: "",
    data_scadenza: "",
    periodicita_mesi: "",
    note: "",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

// Spiegazione contestuale per i tipi del catalogo (per "codice").
const DOC_HELP = {
    DVR: "Documento di Valutazione dei Rischi. Emissione = data di firma/revisione. Si aggiorna al variare di rischi/organizzazione: non ha una scadenza fissa, ma è buona prassi rivederlo periodicamente (puoi impostare una periodicità di promemoria, es. 12 mesi).",
    NOMINA_RSPP: "Atto con cui il datore di lavoro nomina il Responsabile del Servizio di Prevenzione e Protezione. Emissione = data della nomina. Di norma resta valida finché non viene revocata: lascia la scadenza/periodicità vuote.",
    NOMINA_MC: "Nomina del Medico Competente. Emissione = data della nomina. Valida fino a revoca: scadenza/periodicità non obbligatorie.",
    DES_ANTINCENDIO: "Atto di designazione degli addetti alla squadra antincendio. Emissione = data della designazione. Valida finché non cambia la squadra: di solito senza scadenza (gli aggiornamenti formativi degli addetti si tracciano negli Attestati, non qui).",
    DES_PRIMO_SOCC: "Atto di designazione degli addetti al primo soccorso. Emissione = data della designazione. Di norma senza scadenza (la formazione/aggiornamento degli addetti è negli Attestati).",
    RLS_DESIGN: "Verbale di elezione/designazione del Rappresentante dei Lavoratori per la Sicurezza. Emissione = data del verbale. In genere senza scadenza.",
    PIANO_EMERGENZA: "Piano di emergenza ed evacuazione. Emissione = data di redazione/revisione. Va aggiornato a fronte di modifiche: periodicità di promemoria facoltativa.",
    DUVRI: "Documento Unico di Valutazione dei Rischi da Interferenze (appalti). Emissione = data di redazione. Legato alla durata dell'appalto: puoi impostare la scadenza fine appalto.",
    REG_ANTINCENDIO: "Registro dei controlli periodici antincendio. Documento gestionale continuativo: emissione = data di apertura registro.",
    POS: "Piano Operativo di Sicurezza (cantieri). Emissione = data redazione; scadenza = fine lavori/cantiere.",
    CONSEGNA_DPI: "Verbale di consegna dei Dispositivi di Protezione Individuale al lavoratore. Emissione = data di consegna. Di norma senza scadenza (nuova consegna = nuovo documento).",
    INFO_RISCHI: "Modulo di informazione/consegna sui rischi specifici della mansione. Emissione = data di consegna.",
    IDONEITA_SANIT: "Copia del giudizio di idoneità sanitaria. Emissione = data della visita; scadenza = prossima visita (di solito gestita nel tab Sorveglianza sanitaria).",
    CONTRATTO: "Contratto o lettera di assunzione del lavoratore. Emissione = data di stipula.",
};

/**
 * DocumentiPanel
 * Pannello riutilizzabile per l'archivio documentale, sia a livello AZIENDA
 * che LAVORATORE. Mostra elenco con stato scadenza, form di caricamento
 * (con eventuale file) ed eliminazione.
 *
 * Props:
 *  - ambito: "AZIENDA" | "LAVORATORE"
 *  - idAzienda: number (richiesto se ambito = AZIENDA)
 *  - idLavoratore: number (richiesto se ambito = LAVORATORE)
 */
export default function DocumentiPanel({ ambito, idAzienda, idLavoratore, settore }) {
    const entityId = ambito === "LAVORATORE" ? idLavoratore : idAzienda;
    const [tipi, setTipi] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY_DOC);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadTipi = useCallback(async () => {
        try {
            const resp = await SafetyDocumentiService.getTipi(ambito, settore);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setTipi(Array.isArray(data) ? data : []);
        } catch {
            setTipi([]);
        }
    }, [ambito, settore]);

    const loadDocs = useCallback(async () => {
        if (!entityId) { setDocs([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = ambito === "LAVORATORE"
                ? await SafetyDocumentiService.byLavoratore(entityId)
                : await SafetyDocumentiService.byAzienda(entityId);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setDocs(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento dei documenti.");
        } finally {
            setLoading(false);
        }
    }, [ambito, entityId]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadDocs(); }, [loadDocs]);

    const openForm = () => {
        setForm(EMPTY_DOC);
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        if (!entityId) return;
        if (!form.id_tipo_doc && !form.titolo) {
            setError("Seleziona un tipo o indica un titolo.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("ambito", ambito);
            if (ambito === "LAVORATORE") fd.append("id_lavoratore", entityId);
            else fd.append("id_azienda", entityId);
            if (form.id_tipo_doc)       fd.append("id_tipo_doc", form.id_tipo_doc);
            if (form.titolo)            fd.append("titolo", form.titolo);
            if (form.numero_protocollo) fd.append("numero_protocollo", form.numero_protocollo);
            if (form.ente_emittente)    fd.append("ente_emittente", form.ente_emittente);
            if (form.data_emissione)    fd.append("data_emissione", form.data_emissione);
            if (form.data_scadenza)     fd.append("data_scadenza", form.data_scadenza);
            if (form.periodicita_mesi)  fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.note)              fd.append("note", form.note);
            if (file)                   fd.append("file", file);
            await SafetyDocumentiService.create(fd);
            setMsg("Documento aggiunto.");
            setShow(false);
            setForm(EMPTY_DOC);
            setFile(null);
            await loadDocs();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio del documento.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (d) => {
        setError(null);
        try {
            const fn = `${(d.titolo || d.tipo_denominazione || "documento").toString().replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyDocumentiService.download(d.id_documento, fn);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il documento (file non disponibile).");
        }
    };

    const onDelete = async (d) => {
        if (!d?.id_documento) return;
        if (!window.confirm("Eliminare questo documento?")) return;
        setError(null);
        try {
            await SafetyDocumentiService.deleteDocumento(d.id_documento);
            await loadDocs();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione del documento.");
        }
    };

    // Tipi obbligatori non ancora presenti -> checklist "mancanti"
    const presentTipoIds = new Set(docs.filter(d => d.id_tipo_doc).map(d => Number(d.id_tipo_doc)));
    const mancanti = tipi.filter(t => Number(t.obbligatorio) === 1 && !presentTipoIds.has(Number(t.id_tipo_doc)));

    // Tipo attualmente selezionato nel form (per aiuto contestuale)
    const tipoSel = form.id_tipo_doc ? tipi.find(t => Number(t.id_tipo_doc) === Number(form.id_tipo_doc)) : null;
    const tipoHelp = tipoSel ? DOC_HELP[tipoSel.codice] : null;

    if (!entityId) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                <i className="bi bi-info-circle me-1" />
                Salva prima i dati per gestire i documenti.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-folder2-open me-2 text-primary" />
                    Documenti {ambito === "LAVORATORE" ? "del lavoratore" : "aziendali"}
                </h6>
                <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi documento
                </Button>
            </div>

            {mancanti.length > 0 && (
                <BsAlert variant="warning" className="py-2 small">
                    <i className="bi bi-exclamation-triangle me-1" />
                    <strong>Documenti obbligatori mancanti ({mancanti.length}):</strong>{" "}
                    {mancanti.map(m => m.denominazione).join(", ")}.
                </BsAlert>
            )}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">
                        <i className="bi bi-file-earmark-plus me-1 text-primary" />
                        Nuovo documento
                    </div>
                    <div className="text-muted small mb-2">
                        <i className="bi bi-info-circle me-1" />
                        Compila i campi del documento che stai archiviando. Solo <strong>Tipo o Titolo</strong> sono
                        necessari; le altre informazioni sono facoltative e servono a tracciare protocollo e scadenze.
                    </div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Tipo documento</Form.Label>
                            <Form.Select
                                size="sm"
                                value={form.id_tipo_doc}
                                onChange={e => setForm(f => ({ ...f, id_tipo_doc: e.target.value }))}
                            >
                                <option value="">— Nessuno / altro —</option>
                                {tipi.map(t => (
                                    <option key={t.id_tipo_doc} value={t.id_tipo_doc}>
                                        {t.denominazione}{Number(t.obbligatorio) === 1 ? " (obbligatorio)" : ""}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Scegli dal catalogo: imposta automaticamente il nome del documento.
                            </Form.Text>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Titolo</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.titolo}
                                placeholder={tipoSel ? tipoSel.denominazione : "Es. DVR 2026 - Sede di Milano"}
                                onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
                            />
                            <Form.Text className="text-muted">
                                Lascia vuoto per usare il nome del tipo. Compilalo per <strong>distinguere</strong> più
                                documenti dello stesso tipo (es. per sede, anno o revisione).
                            </Form.Text>
                        </Col>

                        {tipoSel && (
                            <Col xs={12}>
                                <BsAlert variant="info" className="py-2 mb-1 small">
                                    <div className="fw-semibold">
                                        <i className="bi bi-lightbulb me-1" />
                                        {tipoSel.denominazione}
                                        {tipoSel.riferimento_normativo
                                            ? <span className="text-muted fw-normal"> — {tipoSel.riferimento_normativo}</span>
                                            : null}
                                    </div>
                                    {tipoHelp || "Indica la data riportata sul documento. La scadenza/periodicità sono facoltative: usale se il documento va rinnovato."}
                                </BsAlert>
                            </Col>
                        )}

                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Data emissione</Form.Label>
                            <Form.Control
                                size="sm"
                                type="date"
                                value={form.data_emissione}
                                onChange={e => setForm(f => ({ ...f, data_emissione: e.target.value }))}
                            />
                            <Form.Text className="text-muted">
                                Data di firma/protocollo del documento.
                            </Form.Text>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Periodicità (mesi)</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                placeholder="es. 12"
                                value={form.periodicita_mesi}
                                onChange={e => setForm(f => ({ ...f, periodicita_mesi: e.target.value }))}
                            />
                            <Form.Text className="text-muted">
                                Ogni quanti mesi rinnovarlo. <strong>Vuoto = nessuna scadenza</strong> (es. nomine).
                            </Form.Text>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Scadenza (opz.)</Form.Label>
                            <Form.Control
                                size="sm"
                                type="date"
                                value={form.data_scadenza}
                                onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))}
                            />
                            <Form.Text className="text-muted">
                                Se vuota, è calcolata da emissione + periodicità.
                            </Form.Text>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">N. protocollo</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.numero_protocollo}
                                onChange={e => setForm(f => ({ ...f, numero_protocollo: e.target.value }))}
                            />
                            <Form.Text className="text-muted">
                                Numero/protocollo, se presente.
                            </Form.Text>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Ente emittente / firmatario</Form.Label>
                            <Form.Control
                                size="sm"
                                placeholder="Es. Datore di lavoro, RSPP, ente formativo…"
                                value={form.ente_emittente}
                                onChange={e => setForm(f => ({ ...f, ente_emittente: e.target.value }))}
                            />
                            <Form.Text className="text-muted">
                                Chi ha emesso o firmato il documento.
                            </Form.Text>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">File (PDF / scansione)</Form.Label>
                            <Form.Control
                                size="sm"
                                type="file"
                                accept="application/pdf,image/*"
                                onChange={e => setFile(e.target.files?.[0] || null)}
                            />
                            <Form.Text className="text-muted">
                                Carica il documento firmato in PDF o immagine.
                            </Form.Text>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                size="sm"
                                value={form.note}
                                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                            />
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button size="sm" variant="secondary" onClick={() => setShow(false)} disabled={saving}>
                            Annulla
                        </Button>
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-check2 me-1" />}
                            Salva documento
                        </Button>
                    </div>
                </div>
            )}

            {error && <BsAlert variant="danger" className="py-2">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2">{msg}</BsAlert>}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento documenti…</div>
            ) : docs.length === 0 ? (
                <div className="text-muted small">
                    Nessun documento caricato. Usa <strong>"Aggiungi documento"</strong> per archiviare i
                    documenti obbligatori (D.Lgs. 81/08).
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Documento</th>
                            <th>Emissione</th>
                            <th>Scadenza</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {docs.map(d => {
                            const stato = d.stato || "PERMANENTE";
                            const badge = stato === "SCADUTO" ? "danger"
                                : stato === "IN_SCADENZA" ? "warning"
                                : stato === "VALIDO" ? "success" : "light";
                            const badgeText = (stato === "SCADUTO" || stato === "VALIDO") ? "light" : "dark";
                            const label = stato === "IN_SCADENZA" ? "In scadenza"
                                : stato === "PERMANENTE" ? "Senza scadenza"
                                : stato.charAt(0) + stato.slice(1).toLowerCase();
                            return (
                                <tr key={d.id_documento}>
                                    <td>
                                        <div>{d.titolo || d.tipo_denominazione || "—"}</div>
                                        {d.tipo_categoria && <div className="text-muted small">{d.tipo_categoria}</div>}
                                    </td>
                                    <td>{fmtDate(d.data_emissione)}</td>
                                    <td>{fmtDate(d.data_scadenza)}</td>
                                    <td>
                                        <Badge bg={badge} text={badgeText} className={badge === "light" ? "border" : undefined}>{label}</Badge>
                                        {typeof d.giorni_alla_scadenza === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                                            <span className="text-muted small ms-1">
                                                ({d.giorni_alla_scadenza < 0
                                                    ? `${Math.abs(d.giorni_alla_scadenza)} gg fa`
                                                    : `tra ${d.giorni_alla_scadenza} gg`})
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {d.path_file ? (
                                            <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(d)} title="Scarica documento">
                                                <i className="bi bi-file-earmark-pdf me-1" />
                                                PDF
                                            </Button>
                                        ) : null}
                                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(d)} title="Elimina documento">
                                            <i className="bi bi-trash" />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
